import React, { useMemo } from 'react';
import { asyncConnect } from 'redux-connect';
import { loadStats } from 'redux/modules/stats';
import Helmet from 'react-helmet';
import config from 'config';
import PricingHeader from 'components/Pricing/PricingHeader/PricingHeader';
import PricingBreakdownBuyer from 'components/Pricing/PricingBreakdown/PricingBreakdownBuyer';
import PricingBreakdownCreative from 'components/Pricing/PricingBreakdown/PricingBreakdownCreative';
import PricingBuyerEnterprise from 'components/Pricing/PricingBuyerEnterprise/PricingBuyerEnterprise';
import PricingBuyerFAQ from 'components/Pricing/PricingFAQ/PricingBuyerFAQ';
import PricingCreativeFAQ from 'components/Pricing/PricingFAQ/PricingCreativeFAQ';
import PricingAreYouACreative from 'components/Pricing/PricingAreYouACreative/PricingAreYouACreative';
import PricingNeedToHire from 'components/Pricing/PricingNeedToHire/PricingNeedToHire';
import LandingGetStarted from 'components/LandingPage/LandingGetStarted/LandingGetStarted';
import PricingTestimonials from 'components/Pricing/PricingTestimonials/PricingTestimonials';
import PricingBreakdownTable from 'components/Pricing/PricingBreakdownTable/PricingBreakdownTable';
import useStats from 'hooks/useStats';
import useAuth from 'hooks/useAuth';
import styles from './Pricing.module.scss';

const PricingBase = ({ location }) => {
  const { user } = useAuth();
  const { count } = useStats();

  const usersCount = (Math.round(count / 10000) * 10000).toLocaleString(
    'en-US',
    { minimumFractionDigits: 0 }
  );

  const creative = useMemo(
    () => location.pathname.match(/creative/),
    [location.pathname]
  );

  let title = 'Plans & Pricing | Twine';
  let description =
    "Twine is free to get started. Explore Twine's simple commission based pricing.";

  if (creative) {
    title = 'Freelancer Plans & Pricing | Twine';
    description =
      "Explore Twine's simple and fairly priced plans for freelancers. Available for everyone. Cancel anytime.";
  }

  return (
    <div>
      <Helmet
        title={title}
        link={[
          {
            rel: 'canonical',
            href:
              config.baseUrl + (creative ? '/pricing/creative' : '/pricing'),
          },
        ]}
        meta={[
          { name: 'description', content: description },
          { property: 'og:description', content: description },
          { name: 'twitter:description', content: description },
        ]}
      />
      <PricingHeader creative={creative} />
      {creative ? (
        <>
          <PricingBreakdownCreative user={user} />
          <div className={styles.socialTrust}>
            <div className={styles.avatarStack}>
              <img
                src="https://thumbs.twinecdn.com/ccde41a2d6cd473c3b6e761249a48c5a/avatar/10301f73-b29e-4da9-b329-27ac67868804/140x140.jpg"
                alt="Avatar 1"
              />
              <img
                src="https://thumbs.twinecdn.com/e4e2ab9212b78dee7903deab5fd25e82/avatar/f2df04c1-fcc0-43fc-b486-603e358dc1ae/140x140.jpg"
                alt="Avatar 2"
              />
              <img
                src="https://thumbs.twinecdn.com/2a8a09fd412ab2877192742a62f44c85/avatar/61dcaf74-6146-4bdc-b85b-b15b8394f0c9/140x140.jpg"
                alt="Avatar 3"
              />
              <img
                src="https://thumbs.twinecdn.com/3d6f662f756bcae270339e95d5cd452f/avatar/71ca058c-a589-4f93-bfcf-a2f71b9dcce2/140x140.jpg"
                alt="Avatar 4"
              />
              <img
                src="https://thumbs.twinecdn.com/8bdc20f913896ee5fff383713a721451/avatar/e89a52a7-f4c1-4bc1-a91a-6ea147946d0c/140x140.jpg"
                alt="Avatar 5"
              />
              <img
                src="https://thumbs.twinecdn.com/b3b3b381fce6d7ddff50fd26ea6a0cda/avatar/98554f33-6788-43c1-874c-dc7c5bbd8cc3/140x140.jpg"
                alt="Avatar 6"
              />
              <img
                src="https://thumbs.twinecdn.com/7863fb3eaa7759c0e409d75a43d44ca4/avatar/3edfc911-0b21-452f-8eae-7353ed2cc67b/140x140.jpg"
                alt="Avatar 7"
              />
              <img
                src="https://thumbs.twinecdn.com/4e24259b17fea5743d4c7b20335f2939/avatar/a15ce572-9800-41b7-9239-2e5779994fa6/140x140.jpg"
                alt="Avatar 8"
              />
            </div>
            <p>❤️ Loved by freelancers, contractors and consultants</p>
          </div>

          <PricingBreakdownTable user={user} />
          <PricingCreativeFAQ className={styles.faq} />
          <PricingTestimonials />
        </>
      ) : (
        <PricingBreakdownBuyer />
      )}
      {!creative && <PricingBuyerEnterprise usersCount={usersCount} />}
      {creative ? null : <PricingBuyerFAQ className={styles.faq} />}
      {!creative && (
        <PricingAreYouACreative gradient className={styles.areYouACreative} />
      )}
      {!creative && (
        <LandingGetStarted
          title={
            creative
              ? 'Build your portfolio and start working today'
              : 'Bring your project to life today'
          }
          linkText="Get started"
          link={creative ? '/signup' : '/post-a-job'}
          gradient="left"
          className={styles.getStarted}
        />
      )}
    </div>
  );
};

const Pricing = asyncConnect([
  {
    promise: ({ store: { dispatch, getState } }) => {
      const promises = [];

      if (!getState().stats.loadedStats) {
        promises.push(dispatch(loadStats()));
      }

      return Promise.all(promises);
    },
  },
])(PricingBase);

export default Pricing;
